import { Trans } from "@lingui/macro";
import { CSSProperties, FC, useState } from "react";
import { Heading, Text } from "@otrium/atoms";
import styles from "./PromotionBanner.module.scss";
import { PromotionBannerProps } from "./PromotionBannerTypes";
import { PromotionBannerImage } from "./PromotionBannerImage";
import { Box } from "@otrium/core";
import classNames from "classnames";
import { theme } from "src/theme";
import { CopyIcon } from "@otrium/icons";

interface PromotionBannerTypeThreeProps extends PromotionBannerProps {
  backgroundColor: string;
  buttonColor: string;
  textColor: string;
  promoCode?: string | null;
  promoCodeColor?: string | null;
}

const PromotionBannerTypeThree: FC<PromotionBannerTypeThreeProps> = ({
  images,
  title,
  description,
  buttonTitle,
  buttonColor,
  buttonTextColor,
  conditionText,
  backgroundColor,
  textColor = "White",
  promoCode,
  promoCodeColor,
  hideCTAButton,
  disableBackgroundColor,
  textAlignment,
  titleColor,
  disablePromoCodeComponent,
  priority,
}) => {
  const [copyState, setCopyState] = useState<"pristine" | "copied" | "errored">(
    "pristine"
  );

  const outlineColor = buttonColor ?? "Black";
  const promoOutline = `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='${encodeURIComponent(
    outlineColor
  )}FF' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`;

  const copyPromoCode = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const code = (
      e.currentTarget as HTMLDivElement
    ).querySelector<HTMLSpanElement>(".code")?.textContent;

    try {
      if (code) {
        await navigator.clipboard.writeText(code);
        setCopyState("copied");
      }
    } catch (err) {
      setCopyState("errored");
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <PromotionBannerImage
        images={images}
        alt={title}
        sizes="100vw"
        priority={priority}
      />
      <div
        className={styles.overlay}
        style={
          {
            "--banner-text-align": textAlignment || "center",
          } as CSSProperties
        }
      >
        <div
          className={styles.bannerContent}
          style={
            {
              "--banner-align": textAlignment || "center",
              "--banner-content-bg":
                !disableBackgroundColor && backgroundColor
                  ? backgroundColor
                  : "transparent",
              "--banner-content-bg-mobile": backgroundColor,
            } as CSSProperties
          }
        >
          <Heading
            as="h1"
            className={styles.bannerHeadingNew}
            color={titleColor || "tone.white"}
            fontWeight="700"
          >
            {title}
          </Heading>
          {description && (
            <Text
              as="p"
              className={styles.text}
              fontSize={"fontSize16"}
              lineHeight={"24px"}
              textAlign="center"
              color={textColor}
            >
              {description}
            </Text>
          )}

          {hideCTAButton ? null : disablePromoCodeComponent ? (
            <div
              className={styles.fakeButton}
              style={
                {
                  "--banner-button-color": buttonTextColor || "tone.white",
                  "--banner-button-bg": buttonColor,
                } as CSSProperties
              }
            >
              {buttonTitle}
            </div>
          ) : (
            <div
              className={styles.promoCodeWrapper}
              style={
                {
                  "--promo-outline": promoOutline,
                  "--promo-code-color": promoCodeColor ?? "Black",
                } as CSSProperties
              }
            >
              <div
                className={styles.promoCode}
                onClick={(e) => void copyPromoCode(e)}
                onMouseLeave={() => setCopyState("pristine")}
              >
                <CopyIcon className="icon" />
                <span className={classNames("code", styles.promoCodeText)}>
                  {promoCode}
                </span>
                <span className={classNames("prompt", styles.promoCodePrompt)}>
                  {copyState === "copied" && <Trans>Copied!</Trans>}
                  {copyState === "pristine" && <Trans>Click to copy</Trans>}
                  {copyState === "errored" && <Trans>Copying failed</Trans>}
                </span>
              </div>
              <div
                className={styles.promoCodeWedge}
                style={
                  {
                    "--promo-wedge-bg": buttonColor ?? theme.colors.tone.black,
                  } as CSSProperties
                }
              />
              <div
                className={styles.promoCodeButton}
                style={
                  {
                    "--promo-button-color": buttonTextColor || "tone.white",
                    "--promo-button-bg": buttonColor ?? theme.colors.tone.black,
                  } as CSSProperties
                }
              >
                {buttonTitle}
              </div>
            </div>
          )}

          {conditionText && (
            <Text
              className={styles.text}
              fontSize="fontSize12"
              color={textColor}
            >
              {conditionText}
            </Text>
          )}
        </div>
      </div>
    </Box>
  );
};

export default PromotionBannerTypeThree;
